import { useState, useRef } from 'react';
import { 
  Clock, Headphones, BadgePercent, Shield, UserCheck, 
  Building, Briefcase, Gift, ClipboardList, Star, MapPin, ChevronLeft, 
  ChevronRight, Filter, Utensils, Ticket, ShoppingBag, Sparkles, Check,
  Plane, Hotel, Bus, Heart, Globe, Award
} from 'lucide-react';

const BenefitsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeCity, setActiveCity] = useState('all');
  const [hoveredBenefit, setHoveredBenefit] = useState(null);
  const carouselRef = useRef(null);

  const whatsappLink = "https://wa.me/522282728556?text=Hola,%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios%20de%20turismo%20académico";

  const companyBenefits = [
    { 
      icon: Clock, 
      title: 'Cotización en 24hrs', 
      desc: 'Propuesta detallada con itinerario completo y presupuesto transparente',
      color: 'from-blue-500 to-cyan-400',
      features: ['Propuesta detallada', 'Itinerario completo', 'Presupuesto transparente']
    },
    { 
      icon: Headphones, 
      title: 'Soporte Integral 24/7', 
      desc: 'Acompañamiento continuo antes, durante y después de tu viaje',
      color: 'from-purple-500 to-pink-400',
      features: ['Línea directa', 'WhatsApp Business', 'Coordinación total']
    },
    { 
      icon: Shield, 
      title: 'Viajes Nacionales - Seguridad', 
      desc: 'Protección completa durante todo el recorrido en territorio nacional',
      color: 'from-emerald-500 to-teal-400',
      features: ['Seguro de viajero en autobús', 'Kit de primeros auxilios', 'Conductores certificados']
    },
    { 
      icon: Globe, 
      title: 'Viajes Internacionales - Cobertura', 
      desc: 'Protección médica internacional con cobertura amplia',
      color: 'from-orange-500 to-amber-400',
      features: ['Seguro de gastos médicos internacional', 'Cobertura amplia', 'Asistencia 24/7 en destino']
    },
    { 
      icon: Plane, 
      title: 'Vuelos Internacionales', 
      desc: 'Vuelos redondos con todo incluido para tu comodidad',
      color: 'from-red-500 to-rose-400',
      features: ['Vuelos redondos', 'Equipaje documentado incluido', 'Conexiones entre países']
    },
    { 
      icon: Hotel, 
      title: 'Hospedaje Premium', 
      desc: 'Hoteles de primera categoría con todas las comodidades',
      color: 'from-indigo-500 to-violet-400',
      features: ['Hoteles 4-5 estrellas', 'Desayuno buffet incluido', 'Ubicación céntrica']
    },
    { 
      icon: Bus, 
      title: 'Transporte de Lujo', 
      desc: 'Autobuses equipados con todas las comodidades para viajes nacionales',
      color: 'from-sky-500 to-blue-400',
      features: ['Autobús de lujo', 'Aire acondicionado', 'WiFi a bordo', 'Sanitarios']
    },
    { 
      icon: MapPin, 
      title: 'Traslados Incluidos', 
      desc: 'Transporte coordinado en viajes internacionales',
      color: 'from-slate-500 to-gray-400',
      features: ['Aeropuerto - Hotel', 'Hotel - Aeropuerto', 'Traslados entre ciudades']
    },
    { 
      icon: Award, 
      title: 'Acceso a Museos y Empresas', 
      desc: 'Entradas y visitas coordinadas a instituciones de prestigio',
      color: 'from-pink-500 to-fuchsia-400',
      features: ['Entradas a museos', 'Visitas empresariales', 'Certificados de participación']
    },
    { 
      icon: BadgePercent, 
      title: 'Tarifas Preferenciales', 
      desc: 'Precios especiales negociados para grupos académicos',
      color: 'from-lime-500 to-green-400',
      features: ['Descuentos grupales', 'Tarifas institucionales', 'Sin costos ocultos']
    },
    { 
      icon: Building, 
      title: 'Alianzas Corporativas', 
      desc: 'Acceso exclusivo a las empresas más importantes',
      color: 'from-yellow-500 to-orange-400',
      features: ['Empresas Fortune 500', 'Networking profesional', 'Vinculación laboral']
    },
    { 
      icon: ClipboardList, 
      title: 'Logística Integral', 
      desc: 'Todo planificado al detalle para que te enfoques en aprender',
      color: 'from-cyan-500 to-teal-400',
      features: ['Itinerario completo', 'Reservaciones confirmadas', 'Coordinación total']
    },
  ];

  const discountPartners = [
    { name: 'Sanborns', category: 'restaurantes', city: 'nacional', discount: '15% en consumo', logo: 'SANBORNS', img: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop' },
    { name: 'Vips', category: 'restaurantes', city: 'nacional', discount: '10% grupos +15', logo: 'VIPS', img: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=400&h=300&fit=crop' },
    { name: 'California Pizza Kitchen', category: 'restaurantes', city: 'cdmx', discount: '12% en grupos', logo: 'CPK', img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=300&fit=crop' },
    { name: 'La Casa de Toño', category: 'restaurantes', city: 'cdmx', discount: 'Menú especial', logo: 'TOÑO', img: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop' },
    { name: 'Six Flags México', category: 'turismo', city: 'cdmx', discount: '20% en boletos', logo: 'SIX FLAGS', img: 'https://images.unsplash.com/photo-1536768139911-e290a59011e4?w=400&h=300&fit=crop' },
    { name: 'Xcaret', category: 'turismo', city: 'cancun', discount: 'Tarifa preferencial', logo: 'XCARET', img: 'https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=400&h=300&fit=crop' },
    { name: 'Acuario Inbursa', category: 'turismo', city: 'cdmx', discount: '15% grupos', logo: 'INBURSA', img: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=300&fit=crop' },
    { name: 'Museo Soumaya', category: 'cultura', city: 'cdmx', discount: 'Visitas guiadas', logo: 'SOUMAYA', img: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=400&h=300&fit=crop' },
    { name: 'Papalote', category: 'cultura', city: 'cdmx', discount: 'Entrada especial', logo: 'PAPALOTE', img: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop' },
    { name: 'Liverpool', category: 'tiendas', city: 'nacional', discount: '10% en compras', logo: 'LIVERPOOL', img: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop' },
    { name: 'Fiesta Americana', category: 'hospedaje', city: 'nacional', discount: 'Tarifas grupales', logo: 'FIESTA', img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop' },
    { name: 'Holiday Inn', category: 'hospedaje', city: 'nacional', discount: '15% reservaciones', logo: 'HOLIDAY', img: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop' },
  ];

  const categories = [
    { id: 'all', label: 'Todos', icon: Filter },
    { id: 'restaurantes', label: 'Restaurantes', icon: Utensils },
    { id: 'turismo', label: 'Turismo', icon: MapPin },
    { id: 'cultura', label: 'Cultura', icon: Ticket },
    { id: 'tiendas', label: 'Tiendas', icon: ShoppingBag },
    { id: 'hospedaje', label: 'Hospedaje', icon: Building },
  ];

  const cities = [
    { id: 'all', label: 'Todas' },
    { id: 'nacional', label: 'Nacional' },
    { id: 'cdmx', label: 'CDMX' },
    { id: 'cancun', label: 'Cancún' },
  ];

  const filteredPartners = discountPartners.filter(partner => {
    const categoryMatch = activeCategory === 'all' || partner.category === activeCategory;
    const cityMatch = activeCity === 'all' || partner.city === activeCity;
    return categoryMatch && cityMatch;
  });

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 340;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <main data-testid="benefits-page" className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1920&q=80"
            alt="Benefits"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="text-[#009DE0]" size={20} />
            <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">
              Beneficios Exclusivos
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            ¿Por qué viajar
            <br />
            <span className="text-[#009DE0]">con nosotros?</span>
          </h1>
          <p className="text-white/70 text-lg mt-6 max-w-xl">
            Más de 5 años de experiencia en turismo académico nos respaldan. Descubre todas las ventajas de viajar con College Traveler.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#009DE0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '200+', label: 'Viajes Realizados', icon: Plane },
              { number: '2,000+', label: 'Estudiantes Felices', icon: Heart },
              { number: '5+', label: 'Años de Experiencia', icon: Star },
              { number: '24/7', label: 'Soporte Continuo', icon: Headphones },
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <stat.icon size={28} className="text-white" />
                </div>
                <div className="text-4xl md:text-5xl font-black text-white mb-2">{stat.number}</div>
                <div className="text-white/80 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Benefits - Interactive Cards */}
      <section className="py-24 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">Nuestras Ventajas</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3">
              Todo lo que incluyen nuestros viajes
            </h2>
            <p className="text-white/50 mt-4 max-w-2xl mx-auto">
              Cada viaje es una experiencia única. Te ofrecemos las mejores condiciones para que tu grupo disfrute al máximo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {companyBenefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`company-benefit-${index}`}
                className="group relative"
                onMouseEnter={() => setHoveredBenefit(index)}
                onMouseLeave={() => setHoveredBenefit(null)}
              >
                {/* Card */}
                <div className={`relative h-full p-6 rounded-2xl bg-[#1A1A1A] border border-white/5 overflow-hidden transition-all duration-500 ${
                  hoveredBenefit === index ? 'border-[#009DE0]/50 transform scale-[1.02]' : 'hover:border-white/10'
                }`}>
                  {/* Gradient Background on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                  
                  {/* Icon */}
                  <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-5 shadow-lg`}>
                    <benefit.icon size={26} className="text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="relative text-lg font-bold text-white mb-2 group-hover:text-[#009DE0] transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="relative text-white/50 text-sm mb-4">{benefit.desc}</p>
                  
                  {/* Features List - Shows on Hover */}
                  <div className={`relative space-y-2 transition-all duration-300 ${
                    hoveredBenefit === index ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'
                  }`}>
                    {benefit.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Check size={14} className="text-[#009DE0]" />
                        <span className="text-white/70">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#009DE0] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all hover:scale-105"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Solicitar Cotización por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Discount Partners Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#009DE0] font-semibold text-sm uppercase tracking-wider">Alianzas y Descuentos</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-3 mb-4">
              Beneficios con nuestros aliados
            </h2>
            <p className="text-[#1A1A1A]/60 max-w-2xl mx-auto">
              Disfruta de descuentos exclusivos en restaurantes, hoteles, parques y tiendas en todo México.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const IconComponent = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    data-testid={`filter-category-${cat.id}`}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      activeCategory === cat.id
                        ? 'bg-[#009DE0] text-white shadow-lg shadow-[#009DE0]/30'
                        : 'bg-[#F5F5F5] text-[#1A1A1A]/70 hover:bg-[#009DE0]/10 hover:text-[#009DE0]'
                    }`}
                  >
                    <IconComponent size={16} />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2 md:ml-auto">
              {cities.map((city) => (
                <button
                  key={city.id}
                  onClick={() => setActiveCity(city.id)}
                  data-testid={`filter-city-${city.id}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeCity === city.id
                      ? 'bg-[#1A1A1A] text-white'
                      : 'bg-[#F5F5F5] text-[#1A1A1A]/70 hover:bg-[#1A1A1A]/10'
                  }`}
                >
                  {city.label}
                </button>
              ))}
            </div>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            <button
              onClick={() => scrollCarousel('left')}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#1A1A1A] shadow-xl hover:border-[#009DE0] hover:text-[#009DE0] transition-all hover:scale-110"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scrollCarousel('right')}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-[#1A1A1A] shadow-xl hover:border-[#009DE0] hover:text-[#009DE0] transition-all hover:scale-110"
            >
              <ChevronRight size={24} />
            </button>

            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide scroll-smooth px-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filteredPartners.map((partner, index) => (
                <a
                  key={index}
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`partner-card-${index}`}
                  className="flex-shrink-0 w-80 group cursor-pointer"
                >
                  {/* Card with Premium Style */}
                  <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 group-hover:shadow-xl group-hover:border-[#009DE0]/30 transition-all duration-500 group-hover:-translate-y-2">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={partner.img}
                        alt={partner.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent" />
                      {/* Discount Badge */}
                      <div className="absolute top-4 right-4 bg-[#009DE0] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                        {partner.discount}
                      </div>
                      {/* City Badge */}
                      <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <MapPin size={12} className="text-[#009DE0]" />
                        <span className="text-xs font-medium text-[#1A1A1A] capitalize">{partner.city}</span>
                      </div>
                    </div>
                    {/* Logo and Info */}
                    <div className="p-6">
                      <p className="text-2xl font-black text-[#1A1A1A] tracking-tight mb-1 group-hover:text-[#009DE0] transition-colors">
                        {partner.name}
                      </p>
                      <p className="text-[#1A1A1A]/50 text-sm capitalize">{partner.category}</p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {filteredPartners.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 bg-[#F5F5F5] rounded-full flex items-center justify-center">
                <Filter size={32} className="text-[#1A1A1A]/30" />
              </div>
              <p className="text-[#1A1A1A]/50 text-lg">No se encontraron aliados con los filtros seleccionados.</p>
              <button 
                onClick={() => { setActiveCategory('all'); setActiveCity('all'); }}
                className="mt-4 text-[#009DE0] font-medium hover:underline"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-[#009DE0]/20 px-4 py-2 rounded-full mb-6">
            <Sparkles size={16} className="text-[#009DE0]" />
            <span className="text-[#009DE0] text-sm font-medium">Cotización Gratis</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para aprovechar todos estos beneficios?
          </h2>
          <p className="text-white/60 text-lg mb-10">
            Solicita tu cotización hoy y comienza a disfrutar de ventajas exclusivas para tu grupo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#009DE0] text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Solicitar Cotización
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BenefitsPage;
